import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { Loader2, ArrowLeft, UserPlus, Trash2 } from 'lucide-react';
import SEO from '@/components/SEO';

type MemberRow = {
  id: string;
  circleId: string;
  userId: string;
  role: string;
  status: string;
  createdAt: string;
};

const ROLES = ['ADMIN', 'FOCUS_OA', 'FAMILY_MEMBER', 'CARE_GIVER'] as const;

export default function CommunityMembersPage() {
  const { circleId } = useParams<{ circleId: string }>();
  const navigate = useNavigate();
  const [members, setMembers] = useState<MemberRow[]>([]);
  const [circleName, setCircleName] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [inviteDialog, setInviteDialog] = useState(false);
  const [inviteUserId, setInviteUserId] = useState('');
  const [inviteRole, setInviteRole] = useState<string>('FAMILY_MEMBER');
  const [removeDialog, setRemoveDialog] = useState<{ open: boolean; member: MemberRow | null }>({ open: false, member: null });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (circleId) { fetchMembers(); fetchCircleName(); }
  }, [circleId]);

  const fetchCircleName = async () => {
    const { data } = await supabase.from('ThriveCircle').select('name').eq('id', circleId!).single();
    if (data) setCircleName(data.name || 'Unknown');
  };

  const fetchMembers = async () => {
    setIsLoading(true);
    const { data, error } = await supabase
      .from('ThriveCircleMember')
      .select('*')
      .eq('circleId', circleId!)
      .order('createdAt', { ascending: false });
    if (error) toast({ title: 'Error', description: error.message, variant: 'destructive' });
    else setMembers(data || []);
    setIsLoading(false);
  };

  const changeRole = async (memberId: string, newRole: string) => {
    const { error } = await supabase.from('ThriveCircleMember').update({
      role: newRole as any,
      updatedAt: new Date().toISOString(),
    }).eq('id', memberId);
    if (error) toast({ title: 'Error', description: error.message, variant: 'destructive' });
    else { toast({ title: 'Role updated' }); fetchMembers(); }
  };

  const inviteMember = async () => {
    if (!inviteUserId.trim()) return;
    setIsSubmitting(true);
    const { error } = await supabase.from('ThriveCircleMember').insert({
      id: crypto.randomUUID(),
      circleId: circleId!,
      userId: inviteUserId.trim(),
      role: inviteRole as any,
      updatedAt: new Date().toISOString(),
    });
    if (error) toast({ title: 'Error', description: error.message, variant: 'destructive' });
    else { toast({ title: 'Member added' }); fetchMembers(); }
    setInviteDialog(false);
    setInviteUserId('');
    setIsSubmitting(false);
  };

  const removeMember = async () => {
    if (!removeDialog.member) return;
    setIsSubmitting(true);
    const { error } = await supabase.from('ThriveCircleMember').delete().eq('id', removeDialog.member.id);
    if (error) toast({ title: 'Error', description: error.message, variant: 'destructive' });
    else { toast({ title: 'Member removed' }); fetchMembers(); }
    setRemoveDialog({ open: false, member: null });
    setIsSubmitting(false);
  };

  const roleBadgeColor = (role: string) => {
    const colors: Record<string, string> = { ADMIN: 'bg-purple-100 text-purple-800', FOCUS_OA: 'bg-blue-100 text-blue-800', FAMILY_MEMBER: 'bg-green-100 text-green-800', CARE_GIVER: 'bg-orange-100 text-orange-800' };
    return colors[role] || '';
  };

  const filtered = members.filter(m => m.userId.toLowerCase().includes(search.toLowerCase()) || m.role.toLowerCase().includes(search.toLowerCase()));

  return (
    <>
      <SEO title={`Members — ${circleName}`} noindex />
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/communities')}>
            <ArrowLeft className="h-4 w-4 mr-1" />Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-foreground">Members</h1>
            <p className="text-muted-foreground mt-1">Managing members of <strong>{circleName}</strong></p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row gap-4 justify-between">
              <div className="relative flex-1 max-w-sm">
                <Input placeholder="Search by user ID or role..." value={search} onChange={e => setSearch(e.target.value)} />
              </div>
              <Button onClick={() => setInviteDialog(true)}>
                <UserPlus className="h-4 w-4 mr-2" />Add Member
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
            ) : filtered.length === 0 ? (
              <p className="text-center text-muted-foreground py-12">No members found.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User ID</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(m => (
                    <TableRow key={m.id}>
                      <TableCell className="font-mono text-sm">{m.userId.slice(0, 12)}…</TableCell>
                      <TableCell>
                        <Select value={m.role} onValueChange={val => changeRole(m.id, val)}>
                          <SelectTrigger className="w-40"><Badge className={roleBadgeColor(m.role)}>{m.role}</Badge></SelectTrigger>
                          <SelectContent>
                            {ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell><Badge variant="outline">{m.status}</Badge></TableCell>
                      
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setRemoveDialog({ open: true, member: m })}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Invite Dialog */}
      <Dialog open={inviteDialog} onOpenChange={setInviteDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Member</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>User ID</Label><Input placeholder="Enter user UUID" value={inviteUserId} onChange={e => setInviteUserId(e.target.value)} /></div>
            <div>
              <Label>Role</Label>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteDialog(false)}>Cancel</Button>
            <Button onClick={inviteMember} disabled={isSubmitting || !inviteUserId.trim()}>
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Add Member
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Confirmation */}
      <Dialog open={removeDialog.open} onOpenChange={open => !open && setRemoveDialog({ open: false, member: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Member</DialogTitle>
            <DialogDescription>Remove this member from the community? This cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemoveDialog({ open: false, member: null })}>Cancel</Button>
            <Button variant="destructive" onClick={removeMember} disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
